<!-- eslint-disable vue/no-unused-refs -->
<template>
    <pendo-popover
        v-if="hasOptions"
        ref="popoverContent"
        :open="isOpen"
        trigger="manual"
        placement="bottom-end"
        :popover-class="['pendo-tools__popover', 'top-nav__popover']"
        @update:open="handlePopoverUpdate">
        <button
            v-pendo-tooltip="{
                content: $t('pendoTools.Pendo tools'),
                arrow: true,
            }"
            class="top-nav__trigger top-nav__button-trigger"
            :class="{
                'is-open': isOpen,
                selected,
            }"
            data-cy="pendo-tools-dropdown-trigger"
            pendo-analytics="pendo-tools-nav-item"
            aria-haspopup
            :aria-label="$t('pendoTools.Pendo tools')"
            :aria-expanded="isOpen.toString()"
            @click="handlePopoverUpdate(!isOpen)"
            @keydown.tab="handleTabKeydown"
            @keydown.space.prevent="handlePopoverUpdate(!isOpen)"
            @keydown.enter.prevent="handlePopoverUpdate(!isOpen)"
            @keydown.left.prevent="$emit('left')"
            @keydown.right.prevent="$emit('right')">
            <pendo-tools-icon />
        </button>
        <template #popover>
            <div
                ref="pendoToolsContent"
                class="top-nav__popover-content"
                data-cy="pendo-tools-dropdown-content"
                @keydown.escape.prevent="handleEscapePopover">
                <h2 class="nav-title">
                    {{ $t('pendoTools.Pendo tools (internal)') }}
                </h2>
                <pendo-router-link
                    v-for="(option, index) in filteredOptions"
                    :key="option.id"
                    tabindex="0"
                    class="nav-option"
                    :pendo-analytics="option.pendoAnalytics"
                    :to="option.route"
                    :target="option.target || '_self'"
                    :external="option.external"
                    @click="handleOptionClick($event)"
                    @keydown.space="handleOptionKeydown($event)"
                    @keydown.enter="handleOptionKeydown($event)"
                    @keydown.tab.prevent="handleOptionKeydown($event)"
                    @keydown.up.prevent="handleArrowKeyNavigation($event, index)"
                    @keydown.down.prevent="handleArrowKeyNavigation($event, index)">
                    <div
                        v-pendo-tooltip="{
                            content: tooltipContent,
                            arrow: true,
                            placement: 'top',
                            delay: { show: 1000, hide: 0 },
                        }"
                        class="nav-option__text"
                        @mouseover="onMouseover($event, option)">
                        {{ option.label }}
                    </div>
                </pendo-router-link>
            </div>
        </template>
    </pendo-popover>
</template>

<script>
import { ref } from 'vue';
import { PendoTooltip, PendoPopover } from '@pendo/components';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import PendoToolsIcon from '@/top-nav/components/icons/pendo-tools-icon.vue';

import {
    hasJobsAndAggsRedirect,
    hasSuperAccessRedirect,
    hasSuperSubsListRedirect,
    hasSuperToolsRedirect,
} from '@/vue-router/route-permissions.utils';
import { getWatcherBucket } from '@/settings/environment.utils';
import { eventIsOpeningANewTabOrWindow } from '@/utils/window.utils.js';
import { elementHasOverflowX } from '@/utils/dom.utils';
import { useArrowKeyNavigation } from '@/common/use/use-arrow-key-navigation/use-arrow-key-navigation';

export default {
    name: 'PendoToolsDropdown',
    components: {
        PendoPopover,
        PendoRouterLink,
        PendoToolsIcon,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        selected: {
            type: Boolean,
            default: false,
        },
        isOpen: {
            type: Boolean,
            default: false,
        },
        preventTabKeyNavigation: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['open', 'close', 'select', 'focusTrigger', 'left', 'right'],
    setup () {
        const pendoToolsContent = ref(null);
        const { handleArrowKeyNavigation } = useArrowKeyNavigation({
            containerRef: pendoToolsContent,
            selector: '.nav-option',
        });

        return {
            pendoToolsContent,
            handleArrowKeyNavigation,
        };
    },
    data () {
        return {
            tooltipContent: null,
        };
    },
    computed: {
        toolOptions () {
            const keyboardShortcut = '⌘ + \\';

            return [
                {
                    id: 'subscriptions',
                    label: !hasSuperAccessRedirect()
                        ? this.$t('pendoTools.Subscriptions ({keyboardShortcut})', { keyboardShortcut })
                        : this.$t('pendoTools.Subscriptions'),
                    route: { name: 'Subscriptions' },
                    pendoAnalytics: 'super-subscriptions',
                    hasPermissions: () => !hasSuperSubsListRedirect(),
                },
                {
                    id: 'jobs',
                    label: this.$t('pendoTools.Job console (Ops)'),
                    route: { name: 'OpsJobs' },
                    pendoAnalytics: 'super-job-console',
                    hasPermissions: () => !hasJobsAndAggsRedirect(),
                },
                {
                    id: 'aggs',
                    label: this.$t('pendoTools.Aggregations (Ops)'),
                    route: { name: 'Aggregations' },
                    pendoAnalytics: 'super-aggregations',
                    hasPermissions: () => !hasJobsAndAggsRedirect(),
                },
                {
                    id: 'agg-builder',
                    label: this.$t('pendoTools.Agg builder'),
                    route: { name: 'AggregationsBuilder' },
                    pendoAnalytics: 'super-agg-builder',
                    hasPermissions: () => !hasSuperAccessRedirect(),
                },
                {
                    id: 'super-tools',
                    label: this.$t('pendoTools.Super tools'),
                    route: { name: 'SuperTools' },
                    pendoAnalytics: 'super-tools',
                    hasPermissions: () => !hasSuperToolsRedirect(),
                },
                {
                    id: 'watcher',
                    label: this.$t('pendoTools.Watcher'),
                    route: getWatcherBucket(),
                    pendoAnalytics: 'super-watcher',
                    hasPermissions: () => !hasSuperAccessRedirect(),
                    target: '_blank',
                    external: true,
                },
            ];
        },
        filteredOptions () {
            return this.toolOptions.filter((option) => {
                if (!option.hasPermissions) {
                    return true;
                }

                return option.hasPermissions();
            });
        },
        hasOptions () {
            return this.filteredOptions.length > 0;
        },
    },
    methods: {
        handleOptionClick ($event) {
            if (eventIsOpeningANewTabOrWindow($event)) return;

            this.handleOptionSelect();
        },
        handleOptionKeydown ($event) {
            if (eventIsOpeningANewTabOrWindow($event)) return;

            $event.preventDefault();
            $event.currentTarget.click();
        },
        handleOptionSelect () {
            this.$emit('select');
        },
        handlePopoverUpdate (open) {
            if (open) {
                this.$emit('open');
                this.focusFirstOption();
            } else {
                this.$emit('close');
            }
        },
        focusFirstOption () {
            setTimeout(() => {
                const firstOption = this.$refs.pendoToolsContent?.querySelector('.nav-option');
                if (firstOption) {
                    firstOption.focus();
                }
            }, 50);
        },
        handleEscapePopover () {
            this.$emit('close');
            this.$emit('focusTrigger');
        },
        onMouseover ({ currentTarget }, option) {
            const { clientWidth, scrollWidth } = currentTarget;
            const hasOverflow = elementHasOverflowX({ clientWidth, scrollWidth });

            if (hasOverflow) {
                this.tooltipContent = option.label;
            } else {
                this.tooltipContent = null;
            }
        },
        handleTabKeydown ($event) {
            if (this.preventTabKeyNavigation) {
                $event.preventDefault();
                this.handlePopoverUpdate(!this.isOpen);
            }
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/side-nav/nav.style.less';

:deep(.pendo-tools-icon) {
    height: 24px;
    width: 24px;
}
</style>

<style lang="less">
.top-nav__popover.pendo-tools__popover {
    width: 210px;
}
</style>
